import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Subscription } from 'rxjs';
import { KeyboardService } from '../keyboard/keyboard.service';
import { Platform } from '@ionic/angular';
import { WORDLIST } from '../../assets/wordlist';

@Component({
  selector: 'tamil-type',
  templateUrl: './type.page.html',
  styleUrls: ['./type.page.scss'],
})

export class typePage implements OnInit, OnDestroy {
  private inputValueSubscription?: Subscription;
  userAttempt: string = '';
  romanTransliteration: string = ''; // Add this line
  showManual: boolean = false;
  selectedSegment = 'rules';
  public audio: HTMLAudioElement | null = null;
  public isPlaying: boolean = false;
  private lastPlayedTime: number = 0;
  private playbackDelay: number = 500; // Delay between pronunciations in milliseconds
  private lastWord: string = '';
  private wordTimeout: any;
  public hasCompleteWord: boolean = false;
  private validWords: Set<string>;
  private _isSystemTamilKeyboard: boolean = false;
  private keyboardTypeSubscription!: Subscription;
  private currentWords: string[] = []; // Add this to track words in the sentence

  // Add this property to control content height
  contentStyle = {
    height: 'calc(100% - 56px)' // Adjust for header height
  };

  constructor(
    private keyboardService: KeyboardService,
    private platform: Platform
  ) {
    this.validWords = new Set(WORDLIST.map(word => word.normalize('NFC')));
    
    // Check for mobile platform
    if (this.isMobilePlatform) {
      // Force system keyboard mode on mobile
      this.keyboardService.setKeyboardType(true);
      this.contentStyle.height = 'calc(100% - 56px - env(safe-area-inset-bottom))';
    }
  }

// Declare a subscriptions array at the class level
private subscriptions: Subscription[] = [];

ngOnInit() {
  this.inputValueSubscription = this.keyboardService.inputValue$.subscribe(
    inputValue => {
      this.userAttempt = inputValue;
      this.updateTransliteration();
    }
  );

  // Store the subscription in the subscriptions array
  const enterKeySubscription = this.keyboardService.enterKeyEvent.subscribe(() => {
    this.updateTransliteration(); // Also update on enter key
  });
  this.subscriptions.push(enterKeySubscription);

  // Add subscription to keyboard type
  this.keyboardTypeSubscription = this.keyboardService.isSystemTamilKeyboard$
    .subscribe(isTamilKeyboard => {
      this._isSystemTamilKeyboard = isTamilKeyboard;
    });
}

onKeyPressed(key: string) {
  this.userAttempt += key;
  this.updateTransliteration(); // Update transliteration
}

updateTransliteration() {
  this.romanTransliteration = this.transliterateToRoman(this.userAttempt);
  
  // Split input by spaces to handle multiple words
  const words = this.userAttempt.trim().split(/\s+/);
  this.currentWords = words;
  
  // Check the last word being typed
  const currentWord = words[words.length - 1];
  
  // Check if the last character is a space
  const endsWithSpace = this.userAttempt.endsWith(' ');
  
  // Check each word for a match
  let matched = false;
  if (currentWord) {
    for (const word of this.validWords) {
      if (this.compareStrings(currentWord, word)) {
        matched = true;
        
        // If we just finished typing a word (space was pressed)
        if (endsWithSpace) {
          this.playAudio(currentWord);
        }
        break;
      }
    }
  }

  this.hasCompleteWord = matched;

  if (this.wordTimeout) {
    clearTimeout(this.wordTimeout);
  }

  // Only play the current word if it's complete and no space was pressed
  if (this.hasCompleteWord && !endsWithSpace) {
    this.playAudio(currentWord);
  }
}

async playAudio(text: string) {
  if (!text.trim()) return;

  const currentTime = Date.now();
  if (currentTime - this.lastPlayedTime < this.playbackDelay) {
    return;
  }

  try {
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'ta-IN';
    utterance.rate = 0.8;
    utterance.volume = 1.0;

    this.isPlaying = true;

    utterance.onend = () => {
      this.isPlaying = false;
      this.lastPlayedTime = Date.now();
    };

    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event.error, event);
      this.isPlaying = false;
    };

    window.speechSynthesis.speak(utterance);

  } catch (error) {
    console.error('Error playing audio:', error);
    this.isPlaying = false;
  }
}

clear() {
  if (this.wordTimeout) {
    clearTimeout(this.wordTimeout);
  }
  window.speechSynthesis.cancel();
  this.userAttempt = '';
  this.romanTransliteration = '';
  this.lastWord = '';
  this.hasCompleteWord = false;
  this.keyboardService.resetInputValue();
}

transliterateToRoman(tamilText: string): string {
  // Mapping for individual characters
  const tamilToRomanMap: { [key: string]: string } = {
    'அ': 'a', 'ஆ': 'ā', 'இ': 'i', 'ஈ': 'ī', 
    'உ': 'u', 'ஊ': 'ū', 'எ': 'e', 'ஏ': 'ē', 
    'ஐ': 'ai', 'ஒ': 'o', 'ஓ': 'ō', 'ஔ': 'au', 
    // Mei without inherent 'a' sound
    'க்': 'k', 'ங்': 'ṅg', 'ச்': 'c', 'ஞ்': 'ñj', 
    'ட்': 'd', 'ண்': 'ṇ', 'த்': 't', 'ந்': 'n', 
    'ப்': 'p', 'ம்': 'm', 'ய்': 'y', 'ர்': 'r', 
    'ல்': 'l', 'வ்': 'v', 'ழ்': 'ḻh', 'ள்': 'ḷ', 
    'ற்': 'ṟ', 'ன்': 'ṉ',
    // Mei with inherent 'a' sound
    'க': 'ka', 'ங': 'ṅga', 'ச': 'ca', 'ஞ': 'ñja', 
    'ட': 'da', 'ண': 'ṇa', 'த': 'ta', 'ந': 'na', 
    'ப': 'pa', 'ம': 'ma', 'ய': 'ya', 'ர': 'ra', 
    'ல': 'la', 'வ': 'va', 'ழ': 'ḻha', 'ள': 'ḷa', 
    'ற': 'ṟa', 'ன': 'ṉa',
  };

  // Mapping for uyir markers
  const uyirMarkerMap: { [key: string]: string } = {
    'ா': 'ā', 'ி': 'i', 'ீ': 'ī', 'ு': 'u', 'ூ': 'ū',
    'ெ': 'e', 'ே': 'ē', 'ை': 'ai', 'ொ': 'o', 'ோ': 'ō', 'ௌ': 'au',
    '்': '', // Pulli, used to suppress the inherent uyir
  };

  let transliteratedText = '';
  let i = 0;

  while (i < tamilText.length) {
    const currentChar = tamilText[i];
    const nextChar = tamilText[i + 1];

    if (uyirMarkerMap[nextChar] !== undefined) {
      // Combine consonant with uyir marker
      const combined = tamilToRomanMap[currentChar + '்'] + uyirMarkerMap[nextChar];
      transliteratedText += combined || currentChar + nextChar;
      i += 2; // Skip the next character as it's a uyir marker
    } else {
      // Single character, just transliterate
      transliteratedText += tamilToRomanMap[currentChar] || currentChar;
      i++;
    }
  }

  return transliteratedText;
}

playWord() {
  
  if (this.userAttempt.trim() && this.hasCompleteWord) {
    this.playAudio(this.userAttempt);
  }
}

ngOnDestroy() {
  if (this.wordTimeout) {
    clearTimeout(this.wordTimeout);
  }
  window.speechSynthesis.cancel();
  if (this.inputValueSubscription) {
    this.inputValueSubscription.unsubscribe();
  }
  if (this.keyboardTypeSubscription) {
    this.keyboardTypeSubscription.unsubscribe();
  }
  // ... other cleanup code ...
}

// Update the string comparison method to handle Tamil characters
private compareStrings(str1: string, str2: string): boolean {
  const normalized1 = str1.normalize('NFC');
  const normalized2 = str2.normalize('NFC');
  
  if (normalized1.length !== normalized2.length) {
    return false;
  }
  
  return normalized1 === normalized2;
}

// Update the word validation to use exact matching
private isValidWord(input: string): boolean {
  const trimmed = input.trim();
  for (const word of this.validWords) {
    if (this.compareStrings(trimmed, word)) {
      return true;
    }
  }
  return false;
}

// Add new method to handle input events
onInputChange(event: any) {
  const value = event.target.value;
  const previousValue = this.userAttempt;
  
  // Always use system input handling on mobile
  if (this.isMobilePlatform || this._isSystemTamilKeyboard) {
    this.userAttempt = value;
    this.updateTransliteration();
    return;
  }

  // Rest of the handler for desktop UyirMei Layout...
}

// Helper method to get romanized version of Tamil characters
private getRomanizedChar(char: string): string | null {
  // Maps for basic characters
  const basicMap: { [key: string]: string } = {
    'க': 'ka', 'ச': 'ca', 'ட': 'da', 'த': 'ta', 'ப': 'pa', 'ற': 'ra',
    'ங': 'nga', 'ஞ': 'nya', 'ண': 'Na', 'ந': 'na', 'ம': 'ma', 'ன': 'na',
    'ய': 'ya', 'ர': 'ra', 'ல': 'la', 'வ': 'va', 'ழ': 'lha', 'ள': 'La',
    'அ': 'a', 'ஆ': 'aa', 'இ': 'i', 'ஈ': 'ii', 'உ': 'u', 'ஊ': 'uu',
    'எ': 'e', 'ஏ': 'ee', 'ஐ': 'ai', 'ஒ': 'o', 'ஓ': 'oo', 'ஔ': 'au'
  };

  // Maps for combined characters (UyirMei)
  const uyirMeiMap: { [key: string]: string } = {
    'ா': 'aa', 'ி': 'i', 'ீ': 'ii', 'ு': 'u', 'ூ': 'uu',
    'ெ': 'e', 'ே': 'ee', 'ை': 'ai', 'ொ': 'o', 'ோ': 'oo', 'ௌ': 'au'
  };

  // If it's a single character in the basic map
  if (basicMap[char]) {
    return basicMap[char];
  }

  // If it's a combined character (UyirMei)
  if (char.length > 1 || this.isTamilChar(char)) {
    // Decompose the character into its parts
    const normalized = char.normalize('NFD');
    const base = normalized[0];
    const vowelMark = normalized[1];

    if (base && basicMap[base]) {
      // If there's a vowel mark, combine the base with the vowel sound
      if (vowelMark && uyirMeiMap[vowelMark]) {
        return basicMap[base].replace(/a$/, '') + uyirMeiMap[vowelMark];
      }
      return basicMap[base];
    }
  }

  return null;
}

// Helper method to identify Tamil characters
private isTamilChar(char: string): boolean {
  const tamilRange = /[\u0B80-\u0BFF]/; // Unicode range for Tamil characters
  return tamilRange.test(char);
}

// Helper method to check if a character is a Mei (consonant)
private isMei(char: string): boolean {
  return ['க்', 'ச்', 'ட்', 'த்', 'ப்', 'ற்', 'ங்', 'ஞ்', 'ண்', 'ந்', 'ம்', 'ன்', 
          'ய்', 'ர்', 'ல்', 'வ்', 'ழ்', 'ள்'].includes(char);
}

// Helper method to check if a character is an Uyir (vowel)
private isUyir(char: string): boolean {
  return ['அ', 'ஆ', 'இ', 'ஈ', 'உ', 'ஊ', 'எ', 'ஏ', 'ஐ', 'ஒ', 'ஓ', 'ஔ'].includes(char);
}

// Add public getter
get isSystemTamilKeyboard(): boolean {
  return this._isSystemTamilKeyboard;
}

// Add public getter for platform checks
get isMobilePlatform(): boolean {
  return this.platform.is('ios') || this.platform.is('android');
}

// Add this method to handle sound icon click
replayWord() {
  if (this.userAttempt.trim()) {
    this.playFullSentence();
  }
}

closeManual() {
  this.showManual = false;
}

// Add a new method to play the entire sentence
async playFullSentence() {
  if (!this.currentWords.length) return;

  try {
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    const fullText = this.currentWords.join(' ');
    const utterance = new SpeechSynthesisUtterance(fullText);
    utterance.lang = 'ta-IN';
    utterance.rate = 0.8;
    utterance.volume = 1.0;

    this.isPlaying = true;

    utterance.onend = () => {
      this.isPlaying = false;
      this.lastPlayedTime = Date.now();
    };

    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event.error, event);
      this.isPlaying = false;
    };

    window.speechSynthesis.speak(utterance);

  } catch (error) {
    console.error('Error playing audio:', error);
    this.isPlaying = false;
  }
}
}